import { LogisticsProjectLookupComponent } from '../../../../logistics-projects/logistics-project-lookup.component';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import * as XLSX from 'xlsx';
import { PageShellComponent } from '../../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../../shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from '../../../../shared/page-layout/page-form-section.component';
import { AccountsService } from '../../../accounts.service';
import { AccountingTaxCode } from '../../../accounting-tax-code.model';
import { recalculateLineTax, taxRateForCode } from '../../../shared/accounting-line-tax';
import { currencyFractionDigits, roundCurrency } from '../../../../shared/utils/commercial-money';
import { accountingNumberWidth } from '../../../shared/accounting-line-width';
import { ManualEntryReasonCode } from '../../../manual-entry-control.model';
import { AccountingAttachmentsPanelComponent } from '../../../shared/accounting-attachments-panel.component';
import { Bill, NonPoBillDraftUpdateRequest } from '../bill.model';
import { canEditNonPoBillDraft } from '../bill-edit-eligibility';
import { SessionStorageService } from '../../../../services/session-storage.service';
import { PaymentTerm } from '../../../../order/models/payment-term.model';
import { LogisticsProjectsService } from '../../../../logistics-projects/logistics-projects.service';
import { CrmClient } from '../../../../crm/crm.model';
import { Party } from '../../../../party/party.model';
import { PartyService } from '../../../../party/party.service';
import { PartyType } from '../../../../party/party-type.enum';
import { ProductsService } from '../../../../order/product.service';
import { Products } from '../../../../order/models/product.model';
import { CrmService } from '../../../../crm/crm.service';
import { CRM_PAYABLE_ROLES, CrmPartyContextService } from '../../../../shared/crm-client-contact-picker/crm-party-context.service';
import { CommercialPartyPickerComponent } from '../../../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from '../../../../shared/commercial-party-picker/commercial-party.model';
import { CrmQuickCreateResult } from '../../../../shared/crm-quick-create/crm-quick-create-dialog.component';

interface ManualBillLine {
    billLineId?: number;
    productId?: number;
    itemNumber?: string;
    itemDescription: string;
    descriptionChoice: Products | string;
    suggestions: Products[];
    reference: string;
    quantity: number;
    unitRate: number | null;
    amountExclTax: number;
    taxAmount: number;
    taxCode: string;
}

@Component({
    selector: 'app-non-po-bill-draft',
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,CommonModule, FormsModule, AutoCompleteModule, ButtonModule, DatePickerModule, FileUploadModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, SelectModule, TextareaModule, ToastModule, PageShellComponent, PageHeroComponent, PageFormSectionComponent, AccountingAttachmentsPanelComponent, CommercialPartyPickerComponent],
    providers: [MessageService],
    templateUrl: './non-po-bill-draft.component.html',
    styleUrls: ['./non-po-bill-draft.component.css', '../../../shared/accounting-line-layout.css']
})
export class NonPoBillDraftComponent implements OnInit {
    readonly numberInputWidth = accountingNumberWidth;
    readonly payablePartyRoles = CRM_PAYABLE_ROLES;
    commercialPartyReference: CommercialPartyReference = { source: 'CRM' };
    reasonCodeOptions: ManualEntryReasonCode[] = [];
    taxCodeOptions: AccountingTaxCode[] = [];
    paymentTermOptions: PaymentTerm[] = [];

    selectedCrmClient?: CrmClient | null;
    selectedParty?: Party | null;
    selectedContactParty?: Party | null;
    catalog: Products[] = [];
    loadingBill = false;
    loadFailed = false;
    loadError = '';
    pendingPartyLookups = 0;
    partySelectionError = false;
    private partyResolutionToken = 0;
    logisticsProjectId?: number;
    vendorInvoiceNumber = '';
    externalSourceReference = '';
    vendorInvoiceDate: Date | null = new Date();
    dueDate: Date | null = new Date();
    paymentTerms = '';
    reasonCode = 'NON_PO_BILL';
    reasonText = '';
    defaultTaxCode = 'VAT';
    duplicateWarning = false;
    documentStatus = 'DRAFT';
    billId?: number;
	private rowVersion?: number;
	saving = false;
    lines: ManualBillLine[] = [this.blankLine()];
    private loadedBill?: Bill;

    constructor(private accountsService: AccountsService, private sessionStorage: SessionStorageService, private messageService: MessageService, private route: ActivatedRoute, private router: Router, private logisticsProjectsService: LogisticsProjectsService, private crmPartyContext: CrmPartyContextService, private partyService: PartyService, private productsService: ProductsService, private crmService: CrmService) {}

    ngOnInit(): void {
        const companyId = this.sessionStorage.getCompany()?.id;
        if (companyId) {
            this.productsService.getSharedCatalog(companyId).subscribe({
                next: products => this.catalog = (products || []).filter(product => !product.deletedAt),
                error: () => this.catalog = []
            });
            this.loadMasterData(companyId);
        }
        this.logisticsProjectId = Number(this.route.snapshot.queryParamMap.get('projectId') || '') || undefined;
        const id = Number(this.route.snapshot.paramMap.get('id'));
        if (id) { this.loadingBill = true; this.loadBill(id); }
    }

    get isEditMode(): boolean { return !!this.billId || !!this.route.snapshot.paramMap.get('id'); }
    get totalExclTax(): number { return this.lines.reduce((sum, line) => sum + Number(line.amountExclTax || 0), 0); }
    get totalTax(): number { return this.lines.reduce((sum, line) => sum + Number(line.taxAmount || 0), 0); }
    get totalAmount(): number { return this.totalExclTax + this.totalTax; }
    get isGenerated(): boolean { return !!this.billId && this.loadedBill?.sourceType?.toUpperCase() !== 'MANUAL'; }
    get canSubmit(): boolean { return !this.loadingBill && !this.loadFailed && !this.pendingPartyLookups && !this.partySelectionError && (!this.billId || this.rowVersion !== undefined) && !!this.selectedParty?.id && (this.isGenerated || (!!this.vendorInvoiceNumber.trim() && !!this.vendorInvoiceDate)) && (this.isGenerated || (!!this.reasonCode && !!this.reasonText.trim())) && this.lines.length > 0 && this.lines.every(line => line.itemDescription.trim() && Number.isFinite(line.quantity) && line.quantity > 0 && line.unitRate !== null && Number.isFinite(line.unitRate) && line.unitRate >= 0 && line.amountExclTax > 0); }

    addLine(): void { if (this.isGenerated) return; this.lines = [...this.lines, this.blankLine()]; }
    removeLine(index: number): void { if (this.isGenerated) return; this.lines = this.lines.length > 1 ? this.lines.filter((_, i) => i !== index) : this.lines; }
    lineTotal(line: ManualBillLine): number { return Number(line.amountExclTax || 0) + Number(line.taxAmount || 0); }
    money(value?: number): string { const digits = currencyFractionDigits(this.currencyCode); return `${this.currencyCode} ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`; }
    lineTabIndex(rowIndex: number, columnIndex: number): number { return 100 + rowIndex * 10 + columnIndex; }
    filterProducts(line: ManualBillLine, query: string): void {
        const term = (query || '').trim().toLocaleLowerCase();
        line.suggestions = this.catalog.filter(product => !term || [product.name, product.productCode, product.description]
            .some(value => value?.toLocaleLowerCase().includes(term))).slice(0, 100);
    }
    onDescriptionInput(line: ManualBillLine, value: Products | string): void {
        if (typeof value !== 'string') return;
        if (value === line.itemDescription) return;
        line.itemDescription = value;
    }
    clearCatalogItem(line: ManualBillLine): void {
        line.productId = undefined;
        line.itemNumber = undefined;
    }
    onProductSelected(line: ManualBillLine, product: Products): void {
        line.productId = product.productID;
        line.itemNumber = product.productCode || undefined;
        line.itemDescription = product.description || product.name;
        line.descriptionChoice = line.itemDescription;
        line.unitRate = Number(product.buyingRate ?? product.standardRate ?? 0);
        this.recalculateLine(line);
    }
    recalculateLine(line: ManualBillLine): void {
        if (line.unitRate === null || !Number.isFinite(line.unitRate) || !Number.isFinite(line.quantity)) return;
        line.amountExclTax = roundCurrency(Math.max(0, line.quantity) * Math.max(0, line.unitRate), this.currencyCode);
        this.recalculateLineTax(line);
    }
    applyDefaultTaxCode(): void {
        this.lines = this.lines.map(line => line.taxCode
            ? line
            : recalculateLineTax({ ...line, taxCode: this.defaultTaxCode }, this.taxCodeOptions, this.currencyCode));
    }
    recalculateLineTax(line: ManualBillLine): void {
        Object.assign(line, recalculateLineTax(line, this.taxCodeOptions, this.currencyCode));
    }

    downloadExcelTemplate(): void {
        const worksheet = XLSX.utils.json_to_sheet([
            ...(this.isGenerated ? this.lines.map(line => ({ billLineId: line.billLineId, description: line.itemDescription, reference: line.reference, quantity: line.quantity, rate: line.unitRate, taxCode: line.taxCode })) : [{ description: 'Vendor service charge', reference: this.vendorInvoiceNumber || 'VENDOR-INV-001', quantity: 1, rate: 100, taxCode: this.defaultTaxCode || 'VAT' }])
        ]);
        const workbook = { Sheets: { 'non_po_bill_lines': worksheet }, SheetNames: ['non_po_bill_lines'] };
        const buffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.downloadWorkbook(buffer, 'non-po-bill-lines-template.xlsx');
    }

    duplicateCheck(): void {
        const companyId = this.sessionStorage.getCompany()?.id;
        const party = this.selectedParty;
        if (!companyId || !party?.id?.entityId) return;
        this.accountsService.checkDuplicateVendorBill(companyId, party.id.entityId, this.vendorInvoiceNumber, this.toIsoDate(this.vendorInvoiceDate), this.totalAmount, this.billId, party.id.entityType).subscribe(result => {
            this.duplicateWarning = result.duplicate;
            this.messageService.add({ severity: result.duplicate ? 'warn' : 'success', summary: result.duplicate ? 'Duplicate' : 'No duplicate', detail: result.duplicate ? 'Vendor invoice appears duplicated.' : 'No duplicate found.' });
        });
    }

    importExcel(event: FileSelectEvent): void {
        const file = event.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const workbook = XLSX.read(reader.result, { type: 'array' });
            const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[workbook.SheetNames[0]]);
            const imported = rows.map(row => this.importedLine(row));
            if (this.isGenerated) {
                const ids = imported.map(line => line.billLineId);
                if (imported.length !== this.lines.length || new Set(ids).size !== this.lines.length
                    || ids.some(id => id == null || !this.lines.some(line => line.billLineId === id))) {
                    this.messageService.add({ severity: 'error', summary: 'Import rejected', detail: 'Generated draft imports must retain every existing billLineId.' });
                    return;
                }
                imported.forEach(line => {
                    const saved = this.lines.find(existing => existing.billLineId === line.billLineId)!;
                    line.reference = saved.reference; line.productId = saved.productId; line.itemNumber = saved.itemNumber;
                });
            }
            this.lines = imported
                .filter(line => line.itemDescription || this.lineTotal(line) > 0);
            if (!this.lines.length) this.lines = [this.blankLine()];
        };
        reader.readAsArrayBuffer(file);
    }

    save(): void {
		if (this.saving) return;
        const company = this.sessionStorage.getCompany();
        const country = this.sessionStorage.getCountryList()?.find(c => c.id === company?.countryId);
        const party = this.selectedParty;
        if (!party?.id) {
            this.messageService.add({ severity: 'error', summary: 'Party required', detail: 'Select the vendor or other payable party before saving.' });
            return;
        }
        if (!company?.id || !country || !this.canSubmit) return;
        const draft: NonPoBillDraftUpdateRequest = {
            externalReferenceNumber: this.externalSourceReference.trim() || null,
            rowVersion: this.rowVersion!, owningCompanyId: company.id, mainParty: party, subParty: this.selectedContactParty || party, crmClientId: this.commercialPartyReference.crmClientId, crmAccountId: this.commercialPartyReference.crmAccountId, crmClientContactId: this.commercialPartyReference.crmContactId, vendorInvoiceNumber: this.vendorInvoiceNumber, vendorInvoiceDate: this.vendorInvoiceDate, dueDate: this.dueDate, paymentTerms: this.paymentTerms, manualReasonCode: this.reasonCode, manualReasonText: this.reasonText, logisticsProjectId: this.logisticsProjectId || undefined, billLines: this.lines.map(line => ({
				billLineId: line.billLineId,
                itemDescription: line.itemDescription,
                itemNumber: line.itemNumber,
                productId: line.productId,
                quantity: line.quantity,
                unitRate: line.unitRate!,
                taxCode: line.taxCode,
                headerReferenceNumber: line.reference || this.vendorInvoiceNumber
            }))
        };
		const createLines = draft.billLines.map((line, index) => ({
            ...line,
            chargeAmount: this.lines[index].amountExclTax,
            taxableAmount: this.lines[index].amountExclTax,
            taxRate: taxRateForCode(line.taxCode, this.taxCodeOptions),
            taxCategory: this.taxCodeOptions.find(code => code.taxCode === line.taxCode)?.taxCategory,
            taxAmount: this.lines[index].taxAmount,
            totalAmount: this.lineTotal(this.lines[index])
        }));
		const bill = { ...draft, billId: this.billId, owningCompany: company, country,
            billDate: this.vendorInvoiceDate, totalAmount: this.totalAmount, taxAmount: this.totalTax,
            sourceType: 'MANUAL', sourceReference: this.vendorInvoiceNumber, manualAttachmentRequired: false,
            documentStatus: 'DRAFT', workflowStatus: 'DRAFT', postingStatus: 'NOT_POSTED', billLines: createLines } as Bill;
		const createdNow = !this.billId;
        const request = this.billId ? this.accountsService.updateBillDraft(this.billId, draft) : this.accountsService.createNonPoBillDraft(bill);
		this.saving = true;
        request.subscribe({
            next: created => {
				this.hydrateBill(created);
                this.messageService.add({ severity: 'success', summary: 'Draft saved', detail: `Bill ${created.billNumber || created.billId} saved.` });
                this.linkProjectRequirementFulfillment(created).add(() => {
					if (createdNow && created.billId) this.router.navigate(['/accounts/enterprise/payables/generated-bills/non-po-draft', created.billId]);
                });
				this.saving = false;
            },
			error: err => {
				this.saving = false;
				const detail = err?.status === 409
					? 'Draft changed elsewhere. The latest version was reloaded.'
					: err?.error?.message || 'Could not save non-PO draft.';
				if (err?.status === 409 && this.billId) this.loadBill(this.billId);
				this.messageService.add({ severity: 'error', summary: 'Save failed', detail });
			}
        });
    }

    private linkProjectRequirementFulfillment(created: Bill) {
        const projectId = Number(this.route.snapshot.queryParamMap.get('projectId') || 0);
        const requirementId = Number(this.route.snapshot.queryParamMap.get('requirementId') || 0);
        const owningCompanyId = Number(this.sessionStorage.getCompany()?.id || 0);
        const targetResourceId = Number(created.billId || this.billId || 0);
        if (!projectId || !requirementId || !owningCompanyId || !targetResourceId) {
            return { add: (callback: () => void) => callback() };
        }
        return this.logisticsProjectsService.linkRequirementFulfillment(projectId, owningCompanyId, requirementId, {
            targetResourceType: this.route.snapshot.queryParamMap.get('targetResourceType') || 'VENDOR_BILL',
            targetResourceId,
            sourceModule: 'ACCOUNTS_PAYABLE',
            sourceReference: created.billNumber || this.vendorInvoiceNumber || `Bill #${targetResourceId}`,
            fulfilledQuantity: Number(this.route.snapshot.queryParamMap.get('quantity') || this.lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0) || 1),
            fulfillmentStatus: 'LINKED',
            status: 'ACTIVE'
        }).subscribe({
            error: error => this.messageService.add({
                severity: 'warn',
                summary: 'Requirement link',
                detail: error?.error?.message || 'Bill was saved, but requirement fulfillment was not linked.'
            })
        });
    }

    back(): void { this.router.navigate(['/accounts/enterprise/payables/generated-bills']); }
    get companyId(): number | undefined { return this.sessionStorage.getCompany()?.id; }
    get actorId(): number | undefined { return this.sessionStorage.getCustomer()?.id; }

    private loadMasterData(companyId: number): void {
        this.accountsService.getManualEntryReasonCodes(companyId, 'NON_PO_VENDOR_BILL').subscribe(codes => {
            this.reasonCodeOptions = codes.length ? codes : [{ documentType: 'NON_PO_VENDOR_BILL', reasonCode: this.reasonCode, description: this.reasonCode }];
        });
        this.accountsService.getAccountingTaxCodes(companyId).subscribe(codes => {
            this.taxCodeOptions = codes.length ? codes : [{ taxCode: 'VAT', description: 'VAT', taxRate: 15 }];
            this.ensureSavedTaxCodes();
            this.defaultTaxCode = this.taxCodeOptions[0]?.taxCode || this.defaultTaxCode;
            this.applyDefaultTaxCode();
        });
        this.accountsService.getPaymentTerms().subscribe(terms => this.paymentTermOptions = terms);
    }

    private loadBill(id: number): void {
        this.loadingBill = true;
        this.loadFailed = false;
        this.loadError = '';
        this.accountsService.getBillById(id).subscribe({
            next: bill => {
                if (!canEditNonPoBillDraft(bill, this.companyId)) {
                    this.loadingBill = false;
                    this.loadFailed = true;
                    this.loadError = 'Only unposted Draft bills for the selected company can be edited on this page.';
                    this.messageService.add({ severity: 'error', summary: 'Draft unavailable', detail: this.loadError });
                    return;
                }
                this.hydrateBill(bill);
                this.loadingBill = false;
                this.loadFailed = false;
            },
            error: () => {
                this.loadingBill = false;
                this.loadFailed = true;
                this.loadError = 'Could not load the vendor bill draft. Return to the bill list and try again.';
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: this.loadError });
            }
        });
    }

	private hydrateBill(bill: Bill): void {
            this.loadedBill = bill;
            this.externalSourceReference = bill.externalReferenceNumber || '';
            this.logisticsProjectId = bill.logisticsProjectId || this.logisticsProjectId;
			this.billId = bill.billId; this.rowVersion = bill.rowVersion; this.documentStatus = bill.documentStatus || 'DRAFT'; this.restoreClientFromBill(bill); this.vendorInvoiceNumber = bill.vendorInvoiceNumber || (this.isGenerated ? '' : bill.billNumber || ''); this.vendorInvoiceDate = bill.vendorInvoiceDate ? new Date(bill.vendorInvoiceDate) : this.isGenerated ? null : bill.billDate ? new Date(bill.billDate) : new Date(); this.dueDate = bill.dueDate ? new Date(bill.dueDate) : null; this.paymentTerms = bill.paymentTerms || ''; this.reasonCode = bill.manualReasonCode || this.reasonCode; this.reasonText = bill.manualReasonText || '';
            this.lines = (bill.billLines || []).map(line => {
                const amountExclTax = Number(line.taxableAmount ?? line.chargeAmount ?? 0);
                const quantity = line.quantity ?? 1;
                const itemDescription = line.itemDescription || '';
                return { billLineId: line.billLineId, productId: line.productId, itemNumber: !line.productId && line.itemNumber === line.taxCode ? undefined : line.itemNumber, itemDescription, descriptionChoice: itemDescription, suggestions: [], reference: line.headerReferenceNumber || this.vendorInvoiceNumber, quantity, unitRate: line.unitRate ?? (quantity > 0 ? Math.round(amountExclTax / quantity * 100_000_000) / 100_000_000 : null), amountExclTax, taxAmount: Number(line.taxAmount || 0), taxCode: line.taxCode || this.defaultTaxCode };
            });
            this.ensureSavedTaxCodes();
            if (!this.lines.length) this.lines = [this.blankLine()];
    }

    private blankLine(): ManualBillLine { return { itemDescription: '', descriptionChoice: '', suggestions: [], reference: '', quantity: 1, unitRate: null, amountExclTax: 0, taxAmount: 0, taxCode: this.defaultTaxCode || 'VAT' }; }
    private ensureSavedTaxCodes(): void {
        if (this.lines.some(line => line.taxCode === 'NON_REGISTERED_VENDOR')
            && !this.taxCodeOptions.some(code => code.taxCode === 'NON_REGISTERED_VENDOR')) {
            this.taxCodeOptions = [...this.taxCodeOptions, { taxCode: 'NON_REGISTERED_VENDOR', description: 'Non-registered vendor', taxRate: 0, taxCategory: 'OUT_OF_SCOPE' }];
        }
    }
    onCrmQuickCreated(result: CrmQuickCreateResult): void {
        if (result.mode === 'client') this.selectedCrmClient = result.record;
    }

    onCommercialPartyChange(reference: CommercialPartyReference): void {
        this.commercialPartyReference = reference;
        const token = ++this.partyResolutionToken;
        this.pendingPartyLookups = 0;
        this.partySelectionError = false;
        this.selectedContactParty = null;
        if (reference.source === 'CRM') {
            if (!reference.crmClientId) { this.selectedParty = null; this.selectedCrmClient = null; return; }
            const existing = this.selectedCrmClient?.id === reference.crmClientId ? this.selectedCrmClient : null;
            if (existing) {
                this.selectedParty = this.crmPartyContext.partyFromClient(existing);
                if (!this.selectedParty) {
                    this.partySelectionError = true;
                    this.messageService.add({ severity: 'error', summary: 'CRM client', detail: 'This CRM client needs a linked Party before it can be billed.' });
                }
                return;
            }
            this.selectedParty = null;
            this.pendingPartyLookups++;
            this.crmService.getClient(reference.crmClientId).subscribe({
                next: response => {
                    if (token !== this.partyResolutionToken) return;
                    this.pendingPartyLookups--;
                    const client = response.data;
                    if (client?.companyId !== this.companyId) { this.partySelectionError = true; return; }
                    this.selectedCrmClient = client;
                    this.selectedParty = this.crmPartyContext.partyFromClient(client);
                    if (!this.selectedParty) {
                        this.partySelectionError = true;
                        this.messageService.add({ severity: 'error', summary: 'CRM client', detail: 'This CRM client needs a linked Party before it can be billed.' });
                    }
                },
                error: () => { if (token === this.partyResolutionToken) { this.pendingPartyLookups--; this.partySelectionError = true; } this.messageService.add({ severity: 'error', summary: 'CRM client', detail: 'Could not resolve the selected CRM client.' }); }
            });
            return;
        }
        this.selectedCrmClient = null;
        const partyId = reference.partyEntityId ?? reference.legacyCompanyId ?? reference.legacyCustomerId;
        const partyType = reference.partyEntityType ?? (reference.legacyCompanyId ? PartyType.Company : PartyType.Customer);
        if (!partyId || !this.companyId) { this.selectedParty = null; return; }
        if (this.selectedParty?.id.entityId !== partyId || this.selectedParty.id.entityType !== partyType) {
            this.selectedParty = null;
            this.pendingPartyLookups++;
            this.partyService.getPartyById(this.companyId, partyType, partyId).subscribe({
                next: party => { if (token === this.partyResolutionToken) { this.selectedParty = party; this.pendingPartyLookups--; } },
                error: () => { if (token === this.partyResolutionToken) { this.pendingPartyLookups--; this.partySelectionError = true; } this.messageService.add({ severity: 'error', summary: 'Party', detail: 'Could not resolve the selected party.' }); }
            });
        }
        const contactId = reference.partyContactEntityId ?? (reference.legacyCompanyId ? reference.legacyCustomerId : undefined);
        const contactType = reference.partyContactEntityType ?? PartyType.Customer;
        if (contactId) {
            this.pendingPartyLookups++;
            this.partyService.getPartyById(this.companyId, contactType, contactId).subscribe({
                next: contact => { if (token === this.partyResolutionToken) { this.selectedContactParty = contact; this.pendingPartyLookups--; } },
                error: () => { if (token === this.partyResolutionToken) { this.pendingPartyLookups--; this.partySelectionError = true; } this.messageService.add({ severity: 'error', summary: 'Party contact', detail: 'Could not resolve the selected contact.' }); }
            });
        }
    }
    private restoreClientFromBill(bill: Bill): void {
        ++this.partyResolutionToken;
        this.pendingPartyLookups = 0;
        this.partySelectionError = false;
        this.selectedParty = bill.mainParty || null;
        this.selectedContactParty = bill.subParty?.id && (bill.subParty.id.entityId !== bill.mainParty?.id?.entityId || bill.subParty.id.entityType !== bill.mainParty?.id?.entityType) ? bill.subParty : null;
        this.selectedCrmClient = bill.crmClient || null;
        if (bill.crmClientId && !bill.crmClient) this.crmService.getClient(bill.crmClientId).subscribe({
            next: response => {
                if (this.loadedBill?.billId === bill.billId && response.data?.companyId === this.companyId) {
                    this.selectedCrmClient = response.data;
                }
            }
        });
        this.commercialPartyReference = {
            source: bill.crmClientId ? 'CRM' : 'PARTY', crmClientId: bill.crmClientId,
            crmAccountId: bill.crmAccountId, crmContactId: bill.crmClientContactId,
            partyEntityId: bill.mainParty?.id?.entityId,
            partyEntityType: bill.mainParty?.id?.entityType,
            partyContactEntityId: this.selectedContactParty?.id.entityId,
            partyContactEntityType: this.selectedContactParty?.id.entityType
        };
    }
    private toIsoDate(date?: Date | null): string | undefined { return date ? date.toISOString().slice(0, 10) : undefined; }
    private importedLine(row: Record<string, unknown>): ManualBillLine {
        const itemDescription = String(this.pick(row, ['description', 'itemDescription', 'item', 'line']) || '');
        const quantity = Number(this.pick(row, ['quantity', 'qty']) ?? 1);
        const amount = roundCurrency(Number(this.pick(row, ['amountExclTax', 'amount', 'netAmount', 'chargeAmount']) ?? 0), this.currencyCode);
        const importedRate = this.pick(row, ['rate', 'unitRate', 'unitPrice']);
        const unitRate = importedRate !== undefined ? Number(importedRate) : quantity > 0 ? Math.round(amount / quantity * 100_000_000) / 100_000_000 : null;
        const savedId = this.pick(row, ['billLineId', 'lineId']);
        const line: ManualBillLine = { billLineId: savedId == null ? undefined : Number(savedId), itemDescription, descriptionChoice: itemDescription, suggestions: [], reference: String(this.pick(row, ['reference', 'sourceReference', 'ref']) || this.vendorInvoiceNumber), quantity, unitRate, amountExclTax: amount, taxAmount: 0, taxCode: String(this.pick(row, ['taxCode', 'tax_code']) || this.defaultTaxCode) };
        if (importedRate !== undefined) this.recalculateLine(line);
        else this.recalculateLineTax(line);
        return line;
    }
    private pick(row: Record<string, unknown>, keys: string[]): unknown { const normalized = Object.entries(row).reduce<Record<string, unknown>>((map, [key, value]) => { map[key.replace(/\s|_/g, '').toLowerCase()] = value; return map; }, {}); return keys.map(key => normalized[key.replace(/\s|_/g, '').toLowerCase()]).find(value => value !== undefined && value !== null && value !== ''); }
    private downloadWorkbook(buffer: ArrayBuffer, fileName: string): void { const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }); const url = window.URL.createObjectURL(blob); const anchor = document.createElement('a'); anchor.href = url; anchor.download = fileName; anchor.click(); window.URL.revokeObjectURL(url); }
    private get currencyCode(): string { return this.sessionStorage.getCountryList()?.find(c => c.id === this.sessionStorage.getCompany()?.countryId)?.currencyCode || 'SAR'; }
}
